import { safePathEncode } from '@/utils/safePath'
import { apiSlice } from './apiSlice'

// ===================== saleContractApi =====================
export interface LookupItem {
  value: string
  label: string
  meta?: Record<string, unknown>
}

export interface LookupResponse {
  items: LookupItem[]
  total: number
}

/** Mirrors FastAPI `SaleContractOut` / write payload (camelCase aliases). */
export interface SaleContract {
  id: number
  contractNo: string
  buyerRef: string | null
  contractDate: string
  clientCode: string
  clientShort?: string | null
  clientName?: string | null
  consigneeCode: string | null
  consigneeName?: string | null
  quantity: number
  packagingCode: string | null
  bags: number | null
  qualityCode: string | null
  certificationCode: string | null
  contractType: string
  fixationCall: string | null
  price: number | null
  priceUnit: string | null
  ptbfDiff: number | null
  vatRate: number | null
  vatApplicable?: boolean | null
  futuresMonth: string | null
  fxRate: number | null
  deliveryTerm: string | null
  warehouse: string
  shipmentMonth?: string | null
  shipmentDateFrom: string | null
  shipmentDateTo: string | null
  deliveryMonth?: string | null
  deliveryDateFrom: string | null
  deliveryDateTo: string | null
  isShipDeli: number | null
  pss: string | null
  paymentWeightTerm: string | null
  franchise: number | null
  paymentTerms: string | null
  conditionsCode: string | null
  note: string | null
  freight: number | null
  logisticsCost: number | null
  insuranceCost?: number | null
  weightingCost?: number | null
  financeCost: number | null
  bankCost: number | null
  description: string | null
  isIncludedRoyal?: boolean | null
  royalFee: number | null
  brokerCode: string | null
  brokerRef?: string | null
  brokerFirm?: string | null
  commission: number | null
  traderCode: string | null
  traderName?: string | null
  packaging?: string | null
  packagingEng?: string | null
  futuresMonth2?: string | null
  fxAuto?: number | null
  qualityPremium?: number | null
  certPremium?: number | null
  packagePremium?: number | null
  g2FobDiffBulk?: number | null
  totalLot?: number | null
  lotFixed?: number | null
  lotToFix?: number | null
  avgFuturesPrice?: number | null
  impliedDiffs?: number | null
  kgFixed?: number | null
  kgToFix?: number | null
  includedRoyal?: string | null
  matchingKg?: number | null
  unmatchedKg?: number | null
  avgPrice?: number | null
  deliveredBag?: number | null
  pendingBag?: number | null
  delivered?: number | null
  pending?: number | null
  tolerance?: number | null
  dateUpdate?: string | null
  personUpdate?: string | null
  finalizedDate?: string | null
  sampCountAccept?: number | null
  sampCountReject?: number | null
  sampCountWaiting?: number | null
  vatTxt?: string | null
  ctrlFilldocTxt?: string | null
  sentContract: boolean
  sentPaper: boolean
  receivedContract: boolean
  receivedPaper: boolean
  isStatus?: number | null
  finalizedText?: string | null
  sentContractText?: string | null
  receivedContractText?: string | null
  sentPaperText?: string | null
  receivedPaperText?: string | null
  saleOffshoreOnshore?: string | null
}

export interface SaleContractListResponse {
  items: SaleContract[]
  total: number
  page: number
  pageSize: number
}

export interface SaleContractListQuery {
  page: number
  pageSize: number
  q?: string
  clientCode?: string
  contractType?: string
  certification?: string
  status?: string
}

/** Access MdQry / §8.5.5 certification premium history rows. */
export interface CertificationPremiumChangeRow {
  changedDate: string | null
  premiumValue: number | null
  dateUpdate: string | null
  personUpdate: string | null
}

export interface CertificationPremiumChangeListResponse {
  items: CertificationPremiumChangeRow[]
}

export type SaleReferenceRow = Record<string, unknown>

export interface SaleReferenceRowsQuery {
  pageKey: string
  limit?: number
}

export interface SaleReferencePageResponse {
  pageKey: string
  sourceForm: string
  sourceQuery: string
  items: SaleReferenceRow[]
  total: number
}

export interface CopyFieldAction {
  field: string
  action: string
  source?: string
}

export interface CopyDraftApiResponse {
  sourceContractSale?: string | null
  mode: string
  draft: SaleContractPayload
  brokerFirm?: string | null
  traderName?: string | null
  fieldActions: CopyFieldAction[]
  mustExtractFromAccess?: string[]
}

export interface PrintOffOptionsResponse {
  contractNo: string
  arbitration?: string | null
  conditions?: string | null
  qualityDescEn?: string | null
  conditionsDesc?: string | null
}

export interface PrintOnOptionsResponse {
  contractNo: string
  quality?: string | null
  paymentDescVn?: string | null
  qualityDescVn?: string | null
}

export interface PrintPreviewResponse {
  title: string
  html: string
  message?: string
}

export interface RepInfoItem {
  id: number
  name?: string | null
  position?: string | null
}

export interface RepInfoResponse {
  items: RepInfoItem[]
  currentSignId?: number | null
}

export interface SaleContractPagePreferences {
  items: RepInfoItem[]
  currentSignId?: number | null
  shipmentPeriodFrom: string
  shipmentPeriodTo: string
  finalizedFilter: 'all' | 'yes' | 'no'
}

type SaleReferenceColumnMode =
  | 'ref-ValueUpdates'
  | 'ref-CertificationUpdates'
  | 'ref-Claims'
  | 'ref-Packages'
  | 'ref-ListandValo'
  | 'ref-DeliveryTerms'
  | 'ref-FuturesAllocation'
  | 'ref-CPList'
  | 'ref-Consignee'
  | 'ref-Certification'
  | 'ref-Ex_Calendar'
  | 'ref-DeliveryPlace'
  | 'ref-FuturesLotAdjust'

/** URL mode segment for `GET|PUT /sale-contracts/preferences/columns/{mode}` — khớp backend `sale_contract_column_preference`. */
export type SaleContractColumnsMode =
  | 'sale-list'
  | 'consignment'
  | 'amendment'
  | 'ptbf-fixation'
  | SaleReferenceColumnMode

export interface SaleContractColumnsPreference {
  columns: string[] | null
  defaultColumns: string[] | null
}

export interface SaleContractListFiltersPatch {
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

export type SaleContractPayload = Omit<
  SaleContract,
  | 'id'
  | 'clientShort'
  | 'clientName'
  | 'consigneeName'
  | 'brokerFirm'
  | 'traderName'
  | 'isStatus'
  | 'finalizedText'
  | 'sentContractText'
  | 'receivedContractText'
  | 'sentPaperText'
  | 'receivedPaperText'
  | 'saleOffshoreOnshore'
>

export const saleContractApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleContracts: builder.query<SaleContractListResponse, SaleContractListQuery>({
      query: ({ page, pageSize, q, clientCode, contractType, certification, status }) => ({
        url: '/sale-contracts',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(clientCode ? { clientCode } : {}),
          ...(contractType ? { contractType } : {}),
          ...(certification ? { certification } : {}),
          ...(status ? { status } : {}),
        },
      }),
      providesTags: ['SaleContract'],
    }),

    getSaleContract: builder.query<SaleContract, string>({
      query: (contractNo) => `/sale-contracts/${safePathEncode(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [{ type: 'SaleContract', id: contractNo }],
    }),

    createSaleContract: builder.mutation<SaleContract, SaleContractPayload>({
      query: (body) => ({ url: '/sale-contracts', method: 'POST', body }),
      invalidatesTags: ['SaleContract'],
    }),

    updateSaleContract: builder.mutation<SaleContract, { id: number; body: SaleContractPayload }>({
      query: ({ id, body }) => ({ url: `/sale-contracts/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleContract'],
    }),

    cancelSaleContract: builder.mutation<SaleContract, number>({
      query: (id) => ({ url: `/sale-contracts/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['SaleContract'],
    }),

    deleteSaleContract: builder.mutation<SaleContract, number>({
      query: (id) => ({ url: `/sale-contracts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleContract'],
    }),

    /** Email-permissioned hard-delete access (legacy `btnDelete` rule, parity with Purchase). */
    getSaleContractCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-contracts/access/can-delete',
    }),

    suggestContractNo: builder.query<{ contractNo: string }, void>({
      query: () => ({ url: '/sale-contracts/suggest-contract-no' }),
      transformResponse: (r: { suggestedNo: string }) => ({ contractNo: r.suggestedNo }),
    }),

    /** Pre-save check (skill access-suggest): contract_sale đã có active row chưa? */
    checkContractSaleExists: builder.query<{ exists: boolean }, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/sale-contracts/check-contract-no',
        params: { contractNo },
      }),
      keepUnusedDataFor: 0,
    }),

    getCertificationPremiumChanges: builder.query<CertificationPremiumChangeListResponse, number | void>({
      query: (limit) => ({
        url: '/sale-contracts/certification-premium-changes',
        ...(limit != null ? { params: { limit } } : {}),
      }),
    }),

    getSaleReferenceRows: builder.query<SaleReferencePageResponse, SaleReferenceRowsQuery>({
      query: ({ pageKey, limit = 1000000 }) => ({
        url: `/references/${encodeURIComponent(pageKey)}`,
        params: { limit },
      }),
      providesTags: (_r, _e, { pageKey }) => [
        { type: 'SaleReference' as const, id: pageKey },
        { type: 'SaleReference' as const, id: 'LIST' },
      ],
    }),

    getRepInfo: builder.query<RepInfoResponse, void>({
      query: () => ({ url: '/sale-contracts/rep-info' }),
      providesTags: ['SaleContract'],
    }),

    getSaleContractPagePreferences: builder.query<SaleContractPagePreferences, void>({
      query: () => ({ url: '/sale-contracts/page-preferences' }),
      providesTags: ['SaleContractPrefs'],
    }),

    getSaleContractColumnsPreference: builder.query<SaleContractColumnsPreference, SaleContractColumnsMode>({
      query: (mode) => ({
        url: `/sale-contracts/preferences/columns/${encodeURIComponent(mode)}`,
      }),
      providesTags: (_r, _e, mode) => [{ type: 'SaleContractColumnPrefs', id: mode }],
    }),

    putSaleContractColumnsPreference: builder.mutation<
      { message: string },
      { mode: SaleContractColumnsMode; columns: string[]; defaultColumns?: string[] }
    >({
      query: ({ mode, columns, defaultColumns }) => ({
        url: `/sale-contracts/preferences/columns/${encodeURIComponent(mode)}`,
        method: 'PUT',
        body: { columns, ...(defaultColumns ? { defaultColumns } : {}) },
      }),
      invalidatesTags: (_r, _e, { mode }) => [{ type: 'SaleContractColumnPrefs', id: mode }],
    }),

    patchSaleContractListFilters: builder.mutation<{ message: string }, SaleContractListFiltersPatch>({
      query: (body) => ({
        url: '/sale-contracts/list-filters',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SaleContract', 'SaleContractPrefs', 'SaleAmendment', 'SalePTBFFixation'],
    }),

    setRepInfo: builder.mutation<{ message: string }, number>({
      query: (signId) => ({
        url: '/sale-contracts/rep-info',
        method: 'POST',
        body: { signId },
      }),
      invalidatesTags: ['SaleContract', 'SaleContractPrefs'],
    }),

    copyDraft: builder.mutation<CopyDraftApiResponse, number>({
      query: (id) => ({ url: `/sale-contracts/${id}/copy-draft`, method: 'POST' }),
    }),

    getPrintOffOptions: builder.query<PrintOffOptionsResponse, string>({
      query: (contractNo) => `/sale-contracts/${safePathEncode(contractNo)}/print-off/options`,
    }),

    getPrintOnOptions: builder.query<PrintOnOptionsResponse, string>({
      query: (contractNo) => `/sale-contracts/${safePathEncode(contractNo)}/print-on/options`,
    }),

    printOffContract: builder.mutation<PrintPreviewResponse, { id: number; body?: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/sale-contracts/${id}/print-off`,
        method: 'POST',
        body: body ?? {},
      }),
    }),

    printOnContract: builder.mutation<PrintPreviewResponse, { id: number; body?: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/sale-contracts/${id}/print-on`,
        method: 'POST',
        body: body ?? {},
      }),
    }),

    getLookupClients: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/clients', params: q ? { q } : {} }),
    }),
    getLookupConsignees: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/consignees', params: q ? { q } : {} }),
    }),
    getLookupPackages: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/packages', params: q ? { q } : {} }),
    }),
    getLookupQualities: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/qualities', params: q ? { q } : {} }),
    }),
    getLookupCertifications: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/certifications',
    }),
    getLookupContractTypes: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/contract-types',
    }),
    getLookupFixationCalls: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/fixation-calls',
    }),
    getLookupPriceUnits: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/price-units',
    }),
    getLookupFuturesMonths: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/futures-months',
    }),
    getLookupDeliveryTerms: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/delivery-terms',
    }),
    getLookupPaymentWeightTerms: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/payment-weight-terms',
    }),
    getLookupConditions: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/conditions',
    }),
    getLookupBrokers: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/brokers', params: q ? { q } : {} }),
    }),
    getLookupTraders: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/traders', params: q ? { q } : {} }),
    }),
    getLookupArbitrations: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/arbitrations', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetSaleContractsQuery,
  useGetSaleContractQuery,
  useCreateSaleContractMutation,
  useUpdateSaleContractMutation,
  useCancelSaleContractMutation,
  useDeleteSaleContractMutation,
  useGetSaleContractCanHardDeleteQuery,
  useSuggestContractNoQuery,
  useLazySuggestContractNoQuery,
  useLazyCheckContractSaleExistsQuery,
  useGetCertificationPremiumChangesQuery,
  useGetSaleReferenceRowsQuery,
  useGetRepInfoQuery,
  useGetSaleContractPagePreferencesQuery,
  useGetSaleContractColumnsPreferenceQuery,
  usePutSaleContractColumnsPreferenceMutation,
  usePatchSaleContractListFiltersMutation,
  useSetRepInfoMutation,
  useCopyDraftMutation,
  useGetPrintOffOptionsQuery,
  useGetPrintOnOptionsQuery,
  usePrintOnContractMutation,
  usePrintOffContractMutation,
  useLazyGetLookupClientsQuery,
  useLazyGetLookupConsigneesQuery,
  useLazyGetLookupPackagesQuery,
  useLazyGetLookupQualitiesQuery,
  useGetLookupCertificationsQuery,
  useGetLookupContractTypesQuery,
  useGetLookupFixationCallsQuery,
  useGetLookupPriceUnitsQuery,
  useGetLookupFuturesMonthsQuery,
  useGetLookupDeliveryTermsQuery,
  useGetLookupPaymentWeightTermsQuery,
  useGetLookupConditionsQuery,
  useLazyGetLookupBrokersQuery,
  useLazyGetLookupTradersQuery,
  useLazyGetLookupArbitrationsQuery,
  useGetLookupArbitrationsQuery,
} = saleContractApi


// ===================== saleContractAmendmentsApi =====================
export interface ContractSnapshot {
  contractNo: string
  contractType?: string | null
  contractQuality?: string | null
  contractQuantity?: number | null
  contractPriceUnit?: string | null
  contractOutrightPrice?: number | null
  contractPtbfDiffs?: number | null
  contractClientName?: string | null
  contractDate?: string | null
  contractBags?: number | null
}

export interface SaleAmendment {
  id?: number | null
  amendmentNo: string
  amendmentDate: string
  contractNo: string
  clientCode?: string | null
  clientName?: string | null
  clientShortName?: string | null
  clientAddress?: string | null
  clientPhone?: string | null
  clientFax?: string | null
  clientVatCode?: string | null
  clientRepresentative?: string | null
  clientPosition?: string | null
  contractDate?: string | null
  contentBeforeChange?: string | null
  contentAfterChange?: string | null
  note?: string | null
  price?: number | null
  quantity?: number | null
  dateUpdate?: string | null
  personUpdate?: string | null
  createdDate?: string | null
  createdBy?: string | null
  updatedDate?: string | null
  updatedBy?: string | null
  isStatus?: number | null
  contractSnapshot?: ContractSnapshot | null
}

export type SaleAmendmentPayload = Pick<
  SaleAmendment,
  | 'amendmentNo'
  | 'amendmentDate'
  | 'contractNo'
  | 'contentBeforeChange'
  | 'contentAfterChange'
  | 'note'
  | 'price'
  | 'quantity'
>

export interface SaleAmendmentListResponse {
  items: SaleAmendment[]
  total: number
  page: number
  pageSize: number
}

export interface SaleAmendmentListQuery {
  page: number
  pageSize: number
  q?: string
}

export interface SaleAmendmentPrintResponse {
  title: string
  html: string
  message?: string
}

export interface SaleAmendmentSeparationResponse {
  sourceContractNo: string
  amendmentNo: string
  mode: string
  draft: SaleContractPayload
  brokerFirm?: string | null
  traderName?: string | null
  fieldActions: Array<{ field: string; action: string; source?: string }>
}

export interface SaleAmendmentContractOptionsResponse {
  items: LookupItem[]
  total: number
}

export const saleAmendmentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleAmendments: builder.query<SaleAmendmentListResponse, SaleAmendmentListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/sale-amendments',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
        },
      }),
      providesTags: ['SaleAmendment'],
    }),

    getSaleAmendment: builder.query<SaleAmendment, string>({
      query: (amendmentNo) => `/sale-amendments/${safePathEncode(amendmentNo)}`,
      providesTags: (_r, _e, amendmentNo) => [{ type: 'SaleAmendment', id: amendmentNo }],
    }),

    createSaleAmendment: builder.mutation<SaleAmendment, SaleAmendmentPayload>({
      query: (body) => ({ url: '/sale-amendments', method: 'POST', body }),
      invalidatesTags: ['SaleAmendment'],
    }),

    updateSaleAmendment: builder.mutation<SaleAmendment, { amendmentNo: string; body: SaleAmendmentPayload }>({
      query: ({ amendmentNo, body }) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SaleAmendment'],
    }),

    cancelSaleAmendment: builder.mutation<SaleAmendment, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['SaleAmendment'],
    }),

    getSaleAmendmentContracts: builder.query<SaleAmendmentContractOptionsResponse, string | void>({
      query: (q) => ({ url: '/sale-amendments/contracts', params: q ? { q } : {} }),
    }),

    getSaleAmendmentContractSnapshot: builder.query<ContractSnapshot, string>({
      query: (contractNo) => `/sale-amendments/contracts/${safePathEncode(contractNo)}/snapshot`,
    }),

    // Auto-gen Annex No theo contract (Offshore→ANNEXxx/…, Onshore→PLxx/…).
    suggestSaleAmendmentNo: builder.query<{ suggestedNo: string }, string>({
      query: (contractNo) => ({ url: '/sale-amendments/suggest-no', params: { contract_no: contractNo } }),
      keepUnusedDataFor: 0,
    }),

    printSaleAmendmentEn: builder.mutation<SaleAmendmentPrintResponse, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/print/en`,
        method: 'POST',
      }),
    }),

    printSaleAmendmentVn: builder.mutation<SaleAmendmentPrintResponse, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/print/vn`,
        method: 'POST',
      }),
    }),

    separateSaleAmendmentContract: builder.mutation<SaleAmendmentSeparationResponse, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/contract-separation`,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useGetSaleAmendmentsQuery,
  useGetSaleAmendmentQuery,
  useCreateSaleAmendmentMutation,
  useUpdateSaleAmendmentMutation,
  useCancelSaleAmendmentMutation,
  useLazyGetSaleAmendmentContractsQuery,
  useGetSaleAmendmentContractSnapshotQuery,
  useSuggestSaleAmendmentNoQuery,
  usePrintSaleAmendmentEnMutation,
  usePrintSaleAmendmentVnMutation,
  useSeparateSaleAmendmentContractMutation,
} = saleAmendmentsApi


// ===================== saleContractConsignmentApi =====================
export interface Consignment {
  id?: number | null
  contractNo: string
  contractDate: string | null
  clientCode: string
  clientName?: string | null
  quantityKg: number | null
  packagingCode?: string | null
  packaging?: string | null
  qualityCode: string
  certification: string
  lastFixingDate: string | null
  dateUpdate?: string | null
  personUpdate?: string | null
  createdDate?: string | null
  createdBy?: string | null
  updatedDate?: string | null
  updatedBy?: string | null
  isStatus?: number | null
}

export type ConsignmentPayload = Pick<
  Consignment,
  | 'contractNo'
  | 'contractDate'
  | 'clientCode'
  | 'quantityKg'
  | 'packagingCode'
  | 'packaging'
  | 'qualityCode'
  | 'certification'
  | 'lastFixingDate'
>

export interface ConsignmentListResponse {
  items: Consignment[]
  total: number
  page: number
  pageSize: number
}

export interface ConsignmentListQuery {
  page: number
  pageSize: number
  q?: string
  clientCode?: string
  certification?: string
}

export const consignmentApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getConsignments: builder.query<ConsignmentListResponse, ConsignmentListQuery>({
      query: ({ page, pageSize, q, clientCode, certification }) => ({
        url: '/consignments',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(clientCode ? { clientCode } : {}),
          ...(certification ? { certification } : {}),
        },
      }),
      providesTags: ['SaleContract'],
    }),
    getConsignment: builder.query<Consignment, string>({
      query: (contractNo) => `/consignments/${safePathEncode(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [{ type: 'SaleContract', id: `consignment-${contractNo}` }],
    }),
    createConsignment: builder.mutation<Consignment, ConsignmentPayload>({
      query: (body) => ({ url: '/consignments', method: 'POST', body }),
      invalidatesTags: ['SaleContract'],
    }),
    updateConsignment: builder.mutation<Consignment, { contractNo: string; body: ConsignmentPayload }>({
      query: ({ contractNo, body }) => ({
        url: `/consignments/${safePathEncode(contractNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SaleContract'],
    }),
    cancelConsignment: builder.mutation<Consignment, string>({
      query: (contractNo) => ({
        url: `/consignments/${safePathEncode(contractNo)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['SaleContract'],
    }),
  }),
})

export const {
  useGetConsignmentsQuery,
  useGetConsignmentQuery,
  useCreateConsignmentMutation,
  useUpdateConsignmentMutation,
  useCancelConsignmentMutation,
} = consignmentApi


// ===================== saleContractPTBFFixationsApi =====================
export interface SalePTBFFixation {
  id?: number | null
  ptbfFixationId?: number | null
  contractNo: string
  contractSale?: string | null
  buyersRef?: string | null
  cpCode?: string | null
  shortName?: string | null
  name?: string | null
  quality?: string | null
  description?: string | null
  quantity?: number | null
  mtons?: number | null
  noOfLots?: number | null
  ptbfDiffs?: number | null
  futuresMonth?: string | null
  balanceLotsTbf?: number | null
  finalPrice?: number | null
  dateFix?: string | null
  releaseDate?: string | null
  lotsFixed?: number | null
  levelFixed?: number | null
  fxRoe?: number | null
  uploadText?: string | null
  customerSignedText?: string | null
  unitTextVn?: string | null
  unitTextEn?: string | null
  finalPriceVnd?: number | null
  finalPriceUsd?: number | null
  qualityDescription?: string | null
  qualityDescriptionOffshore?: string | null
  qualityDescriptionContract?: string | null
  dateUpdate?: string | null
  personUpdate?: string | null
  createdDate?: string | null
  createdBy?: string | null
  updatedDate?: string | null
  updatedBy?: string | null
  isStatus?: number | null
  isUpload?: number | null
  isCusSign?: number | null
  fix1Date?: string | null
  fix1LotsFixed?: number | null
  fix1LevelFixed?: number | null
  fix2Date?: string | null
  fix2LotsFixed?: number | null
  fix2LevelFixed?: number | null
  fix3Date?: string | null
  fix3LotsFixed?: number | null
  fix3LevelFixed?: number | null
  fix4Date?: string | null
  fix4LotsFixed?: number | null
  fix4LevelFixed?: number | null
  fix5Date?: string | null
  fix5LotsFixed?: number | null
  fix5LevelFixed?: number | null
  fix6Date?: string | null
  fix6LotsFixed?: number | null
  fix6LevelFixed?: number | null
  fix7Date?: string | null
  fix7LotsFixed?: number | null
  fix7LevelFixed?: number | null
  fix8Date?: string | null
  fix8LotsFixed?: number | null
  fix8LevelFixed?: number | null
  fix9Date?: string | null
  fix9LotsFixed?: number | null
  fix9LevelFixed?: number | null
  fix10Date?: string | null
  fix10LotsFixed?: number | null
  fix10LevelFixed?: number | null
  fix11Date?: string | null
  fix11LotsFixed?: number | null
  fix11LevelFixed?: number | null
  fix12Date?: string | null
  fix12LotsFixed?: number | null
  fix12LevelFixed?: number | null
  fix13Date?: string | null
  fix13LotsFixed?: number | null
  fix13LevelFixed?: number | null
  fix14Date?: string | null
  fix14LotsFixed?: number | null
  fix14LevelFixed?: number | null
  fix15Date?: string | null
  fix15LotsFixed?: number | null
  fix15LevelFixed?: number | null
  fix16Date?: string | null
  fix16LotsFixed?: number | null
  fix16LevelFixed?: number | null
  fix17Date?: string | null
  fix17LotsFixed?: number | null
  fix17LevelFixed?: number | null
  fix18Date?: string | null
  fix18LotsFixed?: number | null
  fix18LevelFixed?: number | null
  fix19Date?: string | null
  fix19LotsFixed?: number | null
  fix19LevelFixed?: number | null
  fix20Date?: string | null
  fix20LotsFixed?: number | null
  fix20LevelFixed?: number | null
  fix21Date?: string | null
  fix21LotsFixed?: number | null
  fix21LevelFixed?: number | null
  fix22Date?: string | null
  fix22LotsFixed?: number | null
  fix22LevelFixed?: number | null
  fix23Date?: string | null
  fix23LotsFixed?: number | null
  fix23LevelFixed?: number | null
  fix24Date?: string | null
  fix24LotsFixed?: number | null
  fix24LevelFixed?: number | null
  fix25Date?: string | null
  fix25LotsFixed?: number | null
  fix25LevelFixed?: number | null
  fix26Date?: string | null
  fix26LotsFixed?: number | null
  fix26LevelFixed?: number | null
  fix27Date?: string | null
  fix27LotsFixed?: number | null
  fix27LevelFixed?: number | null
  fix28Date?: string | null
  fix28LotsFixed?: number | null
  fix28LevelFixed?: number | null
  fix29Date?: string | null
  fix29LotsFixed?: number | null
  fix29LevelFixed?: number | null
  fix30Date?: string | null
  fix30LotsFixed?: number | null
  fix30LevelFixed?: number | null
}

export interface SalePTBFFixationListResponse {
  items: SalePTBFFixation[]
  total: number
  page: number
  pageSize: number
}

export interface SalePTBFFixationListQuery {
  page: number
  pageSize: number
  q?: string
}

export interface SalePTBFFixationContractOptionsResponse {
  items: LookupItem[]
  total: number
}

export interface SalePTBFFixationLotToFixResponse {
  contractNo: string
  lotToFix?: number | null
  balanceLotsTbf?: number | null
  totalLot?: number | null
}

export interface SalePTBFFixationPayload {
  contractNo: string
  dateFix?: string | null
  releaseDate?: string | null
  lotsFixed?: number | null
  levelFixed?: number | null
  description?: string | null
  fxRoe?: number | null
  isUpload?: number | null
  isCusSign?: number | null
  contractSale?: string | null
  buyersRef?: string | null
  cpCode?: string | null
  shortName?: string | null
  name?: string | null
  quality?: string | null
  quantity?: number | null
  mtons?: number | null
  noOfLots?: number | null
  ptbfDiffs?: number | null
  futuresMonth?: string | null
  balanceLotsTbf?: number | null
  finalPrice?: number | null
  unitTextVn?: string | null
  unitTextEn?: string | null
  finalPriceVnd?: number | null
  finalPriceUsd?: number | null
  qualityDescription?: string | null
  qualityDescriptionOffshore?: string | null
  qualityDescriptionContract?: string | null
}

export interface SalePTBFFixationRow {
  ptbfFixationId?: number | null
  contractNo?: string | null
  dateFix?: string | null
  lotsFixed?: number | null
  levelFixed?: number | null
  createdDate?: string | null
  createdBy?: string | null
  updatedDate?: string | null
  updatedBy?: string | null
}

export interface SalePTBFFixationRowUpdate {
  dateFix?: string | null
  lotsFixed?: number | null
  levelFixed?: number | null
}

export interface SalePTBFFixationRowListResponse {
  items: SalePTBFFixationRow[]
  total: number
}

export interface SalePTBFFixationPrintOptions {
  contractNo: string
  fxRoe?: number | null
  note?: string | null
  noteSource?: string | null
  language?: string | null
}

export interface SalePTBFFixationPrintRequest {
  fxRoe?: number | null
  note?: string | null
}

export interface SalePTBFFixationPrintResponse {
  title: string
  html: string
  message?: string
}

export const salePTBFFixationsApi = apiSlice.enhanceEndpoints({ addTagTypes: ['SalePTBFFixation'] }).injectEndpoints({
  endpoints: (builder) => ({
    getSalePTBFFixations: builder.query<SalePTBFFixationListResponse, SalePTBFFixationListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/sale-ptbf-fixations',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
        },
      }),
      providesTags: ['SalePTBFFixation'],
    }),

    getSalePTBFFixation: builder.query<SalePTBFFixation, string>({
      query: (contractNo) => `/sale-ptbf-fixations/${safePathEncode(contractNo)}/edit-options`,
      providesTags: (_r, _e, contractNo) => [{ type: 'SalePTBFFixation', id: contractNo }],
    }),

    getSalePTBFFixationContracts: builder.query<SalePTBFFixationContractOptionsResponse, string | void>({
      query: (q) => ({ url: '/sale-ptbf-fixations/contracts', params: q ? { q } : {} }),
    }),

    getSalePTBFFixationLotToFix: builder.query<SalePTBFFixationLotToFixResponse, string>({
      query: (contractNo) => `/sale-ptbf-fixations/contracts/${safePathEncode(contractNo)}/lot-to-fix`,
      // Subscribe to 'SalePTBFFixation' invalidation → mutation (create/update/cancel
      // row) sẽ tự trigger refetch lotToFix → field "Lot to Fix" luôn fresh sau Save/Delete.
      providesTags: (_r, _e, contractNo) => [{ type: 'SalePTBFFixation', id: `lot-to-fix:${contractNo}` }, 'SalePTBFFixation'],
    }),

    createSalePTBFFixation: builder.mutation<SalePTBFFixation, SalePTBFFixationPayload>({
      query: (body) => ({ url: '/sale-ptbf-fixations', method: 'POST', body }),
      invalidatesTags: ['SalePTBFFixation'],
    }),

    updateSalePTBFFixationEditOptions: builder.mutation<SalePTBFFixation, { contractNo: string; body: SalePTBFFixationPayload }>({
      query: ({ contractNo, body }) => ({
        url: `/sale-ptbf-fixations/${safePathEncode(contractNo)}/edit-options`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SalePTBFFixation'],
    }),

    cancelSalePTBFFixation: builder.mutation<SalePTBFFixation, number>({
      query: (ptbfFixationId) => ({
        url: `/sale-ptbf-fixations/${ptbfFixationId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['SalePTBFFixation'],
    }),

    getSalePTBFFixationRows: builder.query<SalePTBFFixationRowListResponse, string>({
      query: (contractNo) => `/sale-ptbf-fixations/${safePathEncode(contractNo)}/rows`,
      providesTags: (_r, _e, contractNo) => [{ type: 'SalePTBFFixation', id: `rows:${contractNo}` }],
    }),

    updateSalePTBFFixationRow: builder.mutation<SalePTBFFixationRow, { ptbfFixationId: number; body: SalePTBFFixationRowUpdate }>({
      query: ({ ptbfFixationId, body }) => ({
        url: `/sale-ptbf-fixations/rows/${ptbfFixationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SalePTBFFixation'],
    }),

    cancelSalePTBFFixationByContract: builder.mutation<{ deleted: number }, string>({
      query: (contractNo) => ({
        url: `/sale-ptbf-fixations/${safePathEncode(contractNo)}/cancel-fixations`,
        method: 'POST',
      }),
      invalidatesTags: ['SalePTBFFixation'],
    }),

    getSalePTBFFixationPrintEnOptions: builder.query<SalePTBFFixationPrintOptions, string>({
      query: (contractNo) => `/sale-ptbf-fixations/${safePathEncode(contractNo)}/print/en/options`,
    }),

    getSalePTBFFixationPrintVnOptions: builder.query<SalePTBFFixationPrintOptions, string>({
      query: (contractNo) => `/sale-ptbf-fixations/${safePathEncode(contractNo)}/print/vn/options`,
    }),

    printSalePTBFFixationEn: builder.mutation<SalePTBFFixationPrintResponse, { contractNo: string; body?: SalePTBFFixationPrintRequest }>({
      query: ({ contractNo, body }) => ({
        url: `/sale-ptbf-fixations/${safePathEncode(contractNo)}/print/en`,
        method: 'POST',
        body: body ?? {},
      }),
    }),

    printSalePTBFFixationVn: builder.mutation<SalePTBFFixationPrintResponse, { contractNo: string; body?: SalePTBFFixationPrintRequest }>({
      query: ({ contractNo, body }) => ({
        url: `/sale-ptbf-fixations/${safePathEncode(contractNo)}/print/vn`,
        method: 'POST',
        body: body ?? {},
      }),
    }),
  }),
})

export const {
  useGetSalePTBFFixationsQuery,
  useGetSalePTBFFixationQuery,
  useLazyGetSalePTBFFixationContractsQuery,
  useGetSalePTBFFixationLotToFixQuery,
  useCreateSalePTBFFixationMutation,
  useUpdateSalePTBFFixationEditOptionsMutation,
  useCancelSalePTBFFixationMutation,
  useGetSalePTBFFixationRowsQuery,
  useUpdateSalePTBFFixationRowMutation,
  useCancelSalePTBFFixationByContractMutation,
  useGetSalePTBFFixationPrintEnOptionsQuery,
  useGetSalePTBFFixationPrintVnOptionsQuery,
  usePrintSalePTBFFixationEnMutation,
  usePrintSalePTBFFixationVnMutation,
} = salePTBFFixationsApi
